import React from 'react';
import { motion } from 'framer-motion';
import { Edit3, Copy, CheckCircle, Clock } from 'lucide-react';
import type { DNSRecord } from '../../../../types/dns';

interface DNSRecordCardProps {
  record: DNSRecord;
  zoneName: string;
  isSelected: boolean;
  index: number;
  onToggleSelect: (id: string) => void;
  onEdit?: (record: DNSRecord) => void;
  onCopy: (text: string, recordId: string) => void;
  copySuccess: string | null;
}

const RecordTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const icons = {
    A: '🌍',
    AAAA: '🌐',
    CNAME: '🔗',
    MX: '📧',
    TXT: '📝',
    NS: '🎯',
    SOA: '👑',
    SRV: '⚙️',
    CAA: '🔐',
    PTR: '🔄',
  };
  return <span className="text-lg">{icons[type as keyof typeof icons] || '📄'}</span>;
};

const formatRecordValue = (record: DNSRecord): string => {
  if (record.type === 'MX' && record.priority) {
    return `${record.priority} ${record.value}`;
  }
  if (record.type === 'SRV' && record.priority && record.weight && record.port) {
    return `${record.priority} ${record.weight} ${record.port} ${record.value}`;
  }
  return record.value;
};

const DNSRecordCard: React.FC<DNSRecordCardProps> = ({
  record,
  zoneName,
  isSelected,
  index,
  onToggleSelect,
  onEdit,
  onCopy,
  copySuccess,
}) => {
  return (
    <motion.div
      className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors group"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      whileHover={{ x: 1 }}
    >
      <div className="flex items-center space-x-3 flex-1 min-w-0">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(record.id)}
        />
        <div className="flex-shrink-0">
          <RecordTypeIcon type={record.type} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <span className="text-body font-semibold text-slate-900 dark:text-white truncate">
              {record.name === '@' ? zoneName : record.name}
            </span>
            <span className="text-body-small font-mono bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded">
              {record.type}
            </span>
            {record.proxied && (
              <span className="text-body-small font-mono bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded">
                PROXIED
              </span>
            )}
          </div>
          <p
            className="text-body-small font-mono text-slate-600 dark:text-slate-400 truncate"
            title={formatRecordValue(record)}
          >
            {formatRecordValue(record)}
          </p>
          <div className="flex items-center space-x-4 mt-1 text-xs text-slate-500 dark:text-slate-500">
            {record.ttl && <span>TTL: {record.ttl}s</span>}
            {record.updated_at && (
              <span className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>
                  Updated {new Date(record.updated_at).toLocaleDateString()}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <motion.button
          className="inline-flex items-center justify-center p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded-lg transition-colors focus:outline-none"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onEdit?.(record)}
          title="Edit record"
        >
          <Edit3 className="w-3 h-3" />
        </motion.button>
        <motion.button
          className={`inline-flex items-center justify-center p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-50 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 rounded-lg transition-colors focus:outline-none ${
            copySuccess === record.id ? 'text-green-600 dark:text-green-400' : ''
          }`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => onCopy(formatRecordValue(record), record.id)}
          title={copySuccess === record.id ? 'Copied!' : 'Copy value'}
        >
          {copySuccess === record.id ? (
            <CheckCircle className="w-3 h-3" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default DNSRecordCard;